/**
 * Connectors Service
 * 
 * Handles all API calls for connector integrations.
 * Currently supports: Google Calendar
 * Easily extensible for future connectors (Gmail, Spotify, etc.)
 */

import { getDataAPI, postDataAPI } from '../fetchData';

/**
 * Check connection status for a specific connector
 * @param {string} connectorId - The connector ID (e.g., 'googleCalendar')
 * @param {string} token - Auth token
 * @returns {Promise<{connected: boolean, data: object}>}
 */
export const getConnectionStatus = async (connectorId, token) => {
  try {
    const response = await getDataAPI(`user/connectors/${connectorId}/status`, token);
    
    if (response.status === 200) {
      return {
        connected: response.data.data?.connected || false,
        data: response.data.data?.connection || null
      };
    }
    
    return { connected: false, data: null };
  } catch (error) {
    console.error(`Error checking ${connectorId} status:`, error);
    return { connected: false, data: null };
  }
};

/**
 * Initiate OAuth connection for a connector
 * @param {string} connectorId - The connector ID
 * @param {string} token - Auth token
 * @returns {Promise<string>} - OAuth URL to redirect to
 */
export const initiateConnection = async (connectorId, token) => {
  try {
    const response = await getDataAPI(`user/connectors/${connectorId}/auth-url`, token);
    
    if (response.status === 200) {
      return response.data.data.authUrl;
    }
    
    throw new Error('Failed to get authorization URL');
  } catch (error) {
    console.error(`Error initiating ${connectorId} connection:`, error);
    throw error;
  }
};

/**
 * Disconnect a connector
 * @param {string} connectorId - The connector ID
 * @param {string} token - Auth token
 * @returns {Promise<boolean>}
 */
export const disconnectConnector = async (connectorId, token) => {
  try {
    const response = await postDataAPI(`user/connectors/${connectorId}/disconnect`, {}, token);
    
    if (response.status === 200) {
      return true;
    }
    
    throw new Error('Failed to disconnect');
  } catch (error) {
    console.error(`Error disconnecting ${connectorId}:`, error);
    throw error;
  }
};

/**
 * Test calendar access
 * @param {string} token - Auth token
 * @returns {Promise<{success: boolean}>}
 */
export const testCalendarAccess = async (token) => {
  try {
    const response = await getDataAPI('user/connectors/googleCalendar/test', token);
    
    if (response.status === 200) {
      return { success: true };
    }
    
    return { success: false };
  } catch (error) {
    console.error('Error testing calendar access:', error);
    return { success: false };
  }
};

/**
 * Get list of user's calendars
 * @param {string} token - Auth token
 * @returns {Promise<Array>}
 */
export const getCalendarList = async (token) => {
  try {
    const response = await getDataAPI('user/connectors/googleCalendar/calendars', token);
    
    if (response.status === 200) {
      return response.data.data.calendars || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting calendar list:', error);
    return [];
  }
};

/**
 * Create a calendar event
 * @param {object} eventData - Event data
 * @param {string} eventData.summary - Event title
 * @param {string} eventData.description - Event description
 * @param {string} eventData.location - Event location
 * @param {string} eventData.start - Start datetime (ISO format)
 * @param {string} eventData.end - End datetime (ISO format)
 * @param {string} eventData.calendarId - Calendar ID (optional, uses primary if not specified)
 * @param {string} token - Auth token
 * @returns {Promise<object>}
 */
export const createCalendarEvent = async (eventData, token) => {
  try {
    const response = await postDataAPI('user/connectors/googleCalendar/create-event', eventData, token);
    
    if (response.status === 200) {
      return {
        success: true,
        event: response.data.data.event
      };
    }
    
    throw new Error('Failed to create event');
  } catch (error) {
    console.error('Error creating calendar event:', error);
    throw error;
  }
};

/**
 * Get upcoming events from calendar
 * @param {string} token - Auth token
 * @param {number} maxResults - Maximum number of events to return
 * @returns {Promise<Array>}
 */
export const getUpcomingEvents = async (token, maxResults = 10) => {
  try {
    const response = await getDataAPI(
      `user/connectors/googleCalendar/events?maxResults=${maxResults}`, 
      token
    );
    
    if (response.status === 200) {
      return response.data.data.events || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting upcoming events:', error);
    return [];
  }
};

/**
 * Quick add event to calendar from booking data
 * @param {object} bookingData - Booking data from Dizee
 * @param {string} bookingData.venue - Venue name
 * @param {string} bookingData.date - Event date
 * @param {string} bookingData.time - Event time
 * @param {string} bookingData.location - Event location
 * @param {string} token - Auth token
 * @returns {Promise<object>}
 */
export const quickAddBookingToCalendar = async (bookingData, token) => {
  try {
    const response = await postDataAPI(
      'user/connectors/googleCalendar/quick-add-booking', 
      bookingData, 
      token
    );
    
    if (response.status === 200) {
      return {
        success: true,
        event: response.data.data.event
      };
    }
    
    throw new Error('Failed to add booking to calendar');
  } catch (error) {
    console.error('Error adding booking to calendar:', error);
    throw error;
  }
};

/**
 * Connector configuration presets
 * Useful for managing connector-specific settings
 */
export const CONNECTOR_CONFIGS = {
  googleCalendar: {
    id: 'googleCalendar',
    name: 'Google Calendar',
    description: 'Sync events and create calendar entries',
    icon: '📅',
    color: '#4285F4',
    scopes: [
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/calendar.events'
    ]
  },
  gmail: {
    id: 'gmail',
    name: 'Gmail',
    description: 'Email tracking and AI analysis',
    icon: '✉️',
    color: '#EA4335',
    scopes: [
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.modify'
    ]
  },
  spotifyArtists: {
    id: 'spotifyArtists',
    name: 'Spotify for Artists',
    description: 'Track streams and analytics',
    icon: '🎵',
    color: '#1DB954',
    scopes: ['user-read-email', 'user-read-private']
  }
  // More connectors can be added here
};

/**
 * Get connector configuration by ID
 * @param {string} connectorId - Connector ID
 * @returns {object|null}
 */
export const getConnectorConfig = (connectorId) => {
  return CONNECTOR_CONFIGS[connectorId] || null;
};

const connectorsService = {
  getConnectionStatus,
  initiateConnection,
  disconnectConnector,
  testCalendarAccess,
  getCalendarList,
  createCalendarEvent,
  getUpcomingEvents,
  quickAddBookingToCalendar,
  CONNECTOR_CONFIGS,
  getConnectorConfig
};

export default connectorsService;

